import { Box, FormField, Select } from 'grommet';
import React, { useEffect } from "react";
import useSWR from "swr";
import { silentFetcher } from "../../../../utils/errors";
import MonthPicker from "../../../draft-invoices/MonthPicker";
import {isLastDayOfMonth} from "date-fns";
import { nth, getListOfMonths, formatDateMonth, formatDate, getNDaysFrom, getDateInLocalTime, parseDate } from "../../../../utils/dates";
import { lowercase } from "../../../../utils/string";
import { ISSUE_DATE_PRESET_DATES, SCHEDULE_INVOICE_PRESET_DATES } from "../../../../constants/Dates";
import DateModal from '../../../draft-invoices/DateModal';
import { getDraftSeriesPeriod } from '../../../../utils/draft-invoices';

export const TWICE_A_MONTH_PERIOD_OPTIONS = [
    {value: 'first_and_fifteenth', name: '1st and 15th'},
    {value: 'fifteenth_and_last', name: '15th and last day of the month'}
];

export const FREQUENCY_OPTIONS = [
    {name: "Twice a month", value: 'twice_every_month'},
    {name: "Monthly", value: 'monthly'},
    {name: "Every 3 months", value: 'every_3_months'},
    {name: "Yearly", value: 'yearly'},
];

export const FREQUENCY_EVERY_OPTIONS = [
    {name: "Every month", value: 'twice_every_month'},
    {name: "Every month", value: 'monthly'},
    {name: "Every 3 months", value: 'every_3_months'},
    {name: "Every year", value: 'yearly'},
];

export const getFormattedPeriodLabel = ({frequency, period, start_date}) => {
    if (!frequency) return '';
    if (frequency === "twice_every_month") {
        const option = TWICE_A_MONTH_PERIOD_OPTIONS.find(option => option.value === period);
        return option ? option.name : '';
    }
    // start_date arrives as a string from the API but as a Date once it has been through
    // DateModal, and parseISO throws on anything but a string from date-fns 3.
    const parsedStartDate = parseDate(start_date);
    if ((frequency === "monthly" || frequency === "every_3_months") &&
        (period === "last_day_of_month" || (parsedStartDate && isLastDayOfMonth(parsedStartDate))))
        return "the last day of the month";
    if (frequency === "yearly") return formatDateMonth(new Date(start_date));
    return `${(getDateInLocalTime(start_date)).getDate()}${nth((getDateInLocalTime(start_date)))}`;
}

export const getFormattedDueDateString = (values) => {
    if (!values || !values?.payment_terms) return '';
    let due_date;
    switch(values?.payment_terms){
    case 'fixed':
        return undefined; // don't need an explanation
    case 'default':
        if ((values?.issued_at == null) ) {return `${values?.net_terms} days after the invoice is sent.`}
        due_date = values?.issued_at ? getNDaysFrom(values?.issued_at, values?.net_terms) : getNDaysFrom(new Date(), values?.net_terms);
        return `on ${formatDate(due_date)}`;
    default:
        if (values?.payment_terms === 0) {return "on receipt."}
        if ((values?.issued_at == null) ) {return `${values?.payment_terms} days after the invoice is sent.`}
        due_date = values?.issued_at ? getNDaysFrom(values?.issued_at, values?.payment_terms) : getNDaysFrom(new Date(), values?.payment_terms);
        return `on ${formatDate(due_date)} (Net ${values?.payment_terms})`;
    }
}

export const getFrequencyLabel = (
    {
        frequency,
        start_date
    },
    isLowerCase = false,
    withMonths = false,
    everyAsPrefix = false
) => {
    const name = (everyAsPrefix ? FREQUENCY_EVERY_OPTIONS : FREQUENCY_OPTIONS).find(option => option.value === frequency).name;
    if (!withMonths) return isLowerCase ? lowercase(name) : name;
    if (frequency === "every_3_months") {
        const startMonth = new Date(start_date).getMonth();
        const months = getListOfMonths();
        const issueMonths = [startMonth, startMonth+3, startMonth+6, startMonth+9].map(m => months[m%12].fullName);
        return `every ${issueMonths.join(", ")}`;
    }
    return isLowerCase ? lowercase(name) : name;
};

const RecurringScheduleForm = ({values, errors, setLastInvoiceDate}) => {
    const {
        start_date,
        end_date,
        frequency,
        has_end_date
    } = values?.draft_series || {};

    // fetch balances to display in dashboard aging balance graph
    const {data: invoiceOccurrences} = useSWR(
        start_date && end_date && frequency ?
            `/api/draft-series/occurrences?start_date=${start_date}&end_date=${end_date}&frequency=${frequency}&period=${getDraftSeriesPeriod(values?.draft_series)}` : undefined,
        silentFetcher, // if error out, ignore
        {
            revalidateOnFocus: false,
        }
    );

    useEffect(() => {
        if (!invoiceOccurrences || !invoiceOccurrences?.invoice_dates || !Boolean(has_end_date) || invoiceOccurrences.invoice_dates.length === 0){
            setLastInvoiceDate(undefined)
            return;
        }
        setLastInvoiceDate(new Date(invoiceOccurrences.invoice_dates.slice(-1)[0]));
    }, [invoiceOccurrences, has_end_date]);

    return (
        <>
            <Box pad={{right: 'xsmall'}}>
                <FormField
                    name="frequency"
                    value={values.draft_series.frequency}
                    component={Select}
                    options={FREQUENCY_OPTIONS}
                    labelKey="name"
                    valueKey={{ key: 'value', reduce: true }}
                    label="Invoice frequency"
                    error={errors?.frequency}
                    placeholder={"Select invoice frequency"}
                />
            </Box>
            {values?.draft_series?.frequency === "twice_every_month" ? (
                <Box>
                    <FormField
                        name="period"
                        value={values.draft_series.period}
                        component={Select}
                        options={TWICE_A_MONTH_PERIOD_OPTIONS}
                        labelKey="name"
                        valueKey={{ key: 'value', reduce: true }}
                        label="On"
                        error={errors?.period}
                        placeholder={'Select days'}
                    />
                </Box>
            ) : (<></>)}
            <Box pad={{right: 'xsmall'}}>
                {values?.draft_series.frequency === "twice_every_month" ? (
                    <FormField
                        name="start_date"
                        value={values.draft_series.start_date}
                        format="mm/dd/yyyy"
                        component={MonthPicker}
                        datePresets={ISSUE_DATE_PRESET_DATES}
                        label={"First invoice month"}
                        containerProps={{fill: 'horizontal',}}
                        returnStartDayOfMonth={true}
                        error={errors?.start_date}
                        placeholder={"MM / YYYY"}
                    />
                ) : (
                    <FormField
                        name="start_date"
                        value={values.draft_series.start_date ? new Date(values.draft_series.start_date).toISOString() : undefined}
                        format="mm/dd/yyyy"
                        component={DateModal}
                        datePresets={SCHEDULE_INVOICE_PRESET_DATES}
                        label={"First invoice date"}
                        containerProps={{fill: 'horizontal',}}
                        title="Pick first invoicing date"
                        futureDatesOnly={true}
                        error={errors?.start_date}
                        placeholder={"MM / DD / YYYY"}
                    />
                )}
            </Box>
            <Box direction="row" align="start" gap="xsmall">
                <Box
                    fill={values?.draft_series.has_end_date ? undefined : 'horizontal'}
                    basis={values?.draft_series.has_end_date ? "1/2" : "1"}>
                    <FormField
                        name="has_end_date"
                        value={values.draft_series.has_end_date ? 1 : 0}
                        format="mm/dd/yyyy"
                        component={Select}
                        defaultValue={0}
                        options={[
                            {name: 'Never', value: 0},
                            {name: 'After', value: 1}
                        ]}
                        datePresets={SCHEDULE_INVOICE_PRESET_DATES}
                        label="Ends"
                        containerProps={{fill: 'horizontal',}}
                        labelKey="name"
                        valueKey={{ key: 'value', reduce: true }}
                    />
                </Box>
                {values?.draft_series.has_end_date ? (
                    <Box basis={"1/2"}>
                        <FormField
                            name="end_date"
                            value={values.draft_series.end_date}
                            format="mm/dd/yyyy"
                            component={MonthPicker}
                            datePresets={ISSUE_DATE_PRESET_DATES}
                            label={(<>&nbsp;</>)}
                            containerProps={{fill: 'horizontal',}}
                            returnStartDayOfMonth={false} // we get last day as the bound
                            placeholder="MM / YY"
                        />
                    </Box>
                ) : (<></>)}
            </Box>
        </>
    )
}


export default RecurringScheduleForm;